const qs = require('qs');
const _get = require('lodash/get');
const _omit = require('lodash/omit');
const _isEmpty = require('lodash/isEmpty');
const {
  PAGINATION_PAGE_SIZE,
  CATEGORIES_AND_SERVICES_DISPLAY_KEY_TO_SEARCH_PARAM_MAP,
} = require('../utils/constants');

const transformPagination = (pagination = { page: 1 }) => ({
  page: pagination.page - 1,
  pageSize: PAGINATION_PAGE_SIZE,
});

const removeEmptyValues = obj => {
  const clearedObject = { ...obj };
  Object.keys(clearedObject).forEach(
    key => !clearedObject[key] && delete clearedObject[key],
  );
  return clearedObject;
};

const getSearchParamFromDisplayKey = (
  displayKeys,
  paramsDisplayKeyToSearchParam = CATEGORIES_AND_SERVICES_DISPLAY_KEY_TO_SEARCH_PARAM_MAP,
) => {
  return displayKeys.map(displayKey => {
    return paramsDisplayKeyToSearchParam[displayKey] || displayKey;
  });
};

const getCategoryOrServiceDisplayKeys = (
  searchParams,
  paramsDisplayKeyToSearchParam = CATEGORIES_AND_SERVICES_DISPLAY_KEY_TO_SEARCH_PARAM_MAP,
) =>
  searchParams.split(',').map(sp => {
    const displayKey = Object.keys(paramsDisplayKeyToSearchParam).filter(
      c => paramsDisplayKeyToSearchParam[c] === sp,
    );
    return displayKey.length ? displayKey[0] : sp;
  });

const getCategoryOrServiceIds = (searchParams, categoryServiceMap) =>
  getCategoryOrServiceDisplayKeys(searchParams)
    .map(c => categoryServiceMap[c])
    .filter(c => c);

const transformFilters = (filters = {}) => {
  const lang = _get(filters, 'lang', []).join(',');
  const lower = _get(filters, 'lower');
  const upper = _get(filters, 'upper');
  const serviceIds = getSearchParamFromDisplayKey(
    _get(filters, 'serviceIds', []),
  ).join(',');
  const categoryIds = getSearchParamFromDisplayKey(
    _get(filters, 'categoryIds', []),
  ).join(',');
  const lng = _get(filters, 'location.lng');
  const lat = _get(filters, 'location.lat');
  const country = _get(filters, 'location.country');
  const city = _get(filters, 'location.city');

  return removeEmptyValues({
    lang,
    lower,
    upper,
    serviceIds,
    categoryIds,
    lng,
    lat,
    country,
    city,
  });
};

const transformPageFromQuery = filtersSearchQuery => {
  const queryParams = qs.parse(filtersSearchQuery, { ignoreQueryPrefix: true });
  queryParams.page &&
    (queryParams.page = Number.parseInt(queryParams.page, 10));

  return queryParams.page;
};

const isNoDefaultLocation = searchQuery => {
  const queryParams = qs.parse(searchQuery, { ignoreQueryPrefix: true });
  return queryParams['no-loc'] === 'true';
};

const transformFiltersFromUrl = (
  mainCategory,
  mainService,
  filtersSearchQuery,
) => {
  const filters = transformFiltersFromQuery(filtersSearchQuery);
  if (_isEmpty(filters.categoryIds) && _isEmpty(filters.serviceIds)) {
    mainCategory &&
      !mainService &&
      (filters.categoryIds = getCategoryOrServiceDisplayKeys(mainCategory));
    mainService &&
      (filters.serviceIds = getCategoryOrServiceDisplayKeys(mainService));
  }

  return filters;
};

const transformFiltersFromQuery = filtersSearchQuery => {
  const filters = qs.parse(filtersSearchQuery, { ignoreQueryPrefix: true });
  filters.page && (filters.page = Number.parseInt(filters.page, 10));
  filters.lang && (filters.lang = filters.lang.split(','));
  filters.serviceIds &&
    (filters.serviceIds = getCategoryOrServiceDisplayKeys(filters.serviceIds));
  filters.categoryIds &&
    (filters.categoryIds = getCategoryOrServiceDisplayKeys(
      filters.categoryIds,
    ));
  filters.lower && (filters.lower = Number.parseInt(filters.lower, 10));
  filters.upper && (filters.upper = Number.parseInt(filters.upper, 10));
  filters.lat &&
    filters.lng &&
    filters.country &&
    (filters.city
      ? (filters.location = {
          lat: Number.parseFloat(filters.lat),
          lng: Number.parseFloat(filters.lng),
          country: filters.country,
          city: decodeURIComponent(filters.city),
        })
      : (filters.location = {
          lat: Number.parseFloat(filters.lat),
          lng: Number.parseFloat(filters.lng),
          country: filters.country,
        }));

  return _omit(filters, ['lat', 'lng', 'country', 'city', 'page', 'no-loc']);
};

const getCategoryAndQueryFiltersForUrl = (filters, pagination, categories) => {
  const urlComponents = { mainCategory: '', mainService: '', searchQuery: '' };
  const page = pagination.page > 1 ? pagination.page : null;
  if (!_isEmpty(filters.categoryIds)) {
    urlComponents.mainCategory = `/${
      getSearchParamFromDisplayKey([filters.categoryIds[0]])[0]
    }`;
    if (
      filters.categoryIds &&
      filters.categoryIds.length === 1 &&
      _isEmpty(filters.serviceIds)
    ) {
      filters.categoryIds.shift();
    }
  } else if (!_isEmpty(filters.serviceIds)) {
    urlComponents.mainCategory = `/${
      getSearchParamFromDisplayKey([
        Object.keys(categories).find(category =>
          categories[category].includes(filters.serviceIds[0]),
        ),
      ])[0]
    }`;
  }
  if (!_isEmpty(filters.serviceIds) && _isEmpty(filters.categoryIds)) {
    urlComponents.mainService = `/${
      getSearchParamFromDisplayKey([filters.serviceIds[0]])[0]
    }`;
    if (filters.serviceIds && filters.serviceIds.length === 1) {
      filters.serviceIds.shift();
    }
  }
  urlComponents.searchQuery = qs.stringify(
    { ...transformFilters(filters), page },
    { addQueryPrefix: true, skipNulls: true },
  );
  return urlComponents;
};

const resolveUrlWithFiltersMainCategoryInPath = ({
  path,
  filters,
  pagination,
  categories,
}) => {
  const urlComponents = getCategoryAndQueryFiltersForUrl(
    { ...filters },
    pagination,
    categories,
  );
  return `${path}${urlComponents.mainCategory}${urlComponents.mainService}${urlComponents.searchQuery}`;
};

const isFiltersValid = (
  filters,
  mainCategoryDisplayKey,
  categoriesMap,
  servicesMap,
) => {
  return !(
    !categoriesMap[getCategoryOrServiceDisplayKeys(mainCategoryDisplayKey)] ||
    (filters.serviceIds &&
      filters.serviceIds.filter(
        s => servicesMap[getCategoryOrServiceDisplayKeys(s)] === undefined,
      ).length > 0) ||
    (filters.categoryIds &&
      filters.categoryIds.filter(
        c => categoriesMap[getCategoryOrServiceDisplayKeys(c)] === undefined,
      ).length > 0)
  );
};

const getQueryParamValueFromUrl = (url, param) => {
  const qsParams = qs.parse(url.substr(url.indexOf('?')), {
    ignoreQueryPrefix: true,
  });
  return qsParams[param];
};

module.exports = {
  transformPagination,
  transformFilters,
  transformPageFromQuery,
  transformFiltersFromQuery,
  transformFiltersFromUrl,
  resolveUrlWithFiltersMainCategoryInPath,
  isFiltersValid,
  getCategoryOrServiceIds,
  getCategoryOrServiceDisplayKeys,
  getSearchParamFromDisplayKey,
  isNoDefaultLocation,
  getQueryParamValueFromUrl,
};
